import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { toast } from "react-toastify";
import { apiErrorMessage, bulkImportHierarchy, listCourses } from "../../api/assessmentHierarchy";

const ACCENT = "#17479e";

const TEMPLATE_CSV =
  "Subject,Chapter,Topic,SubTopic\n" +
  "Physics,Kinematics,Projectile Motion,Range and Time of Flight\n" +
  "Physics,Kinematics,Projectile Motion,Maximum Height\n" +
  "Physics,Kinematics,Relative Motion,\n" +
  "Physics,Laws of Motion,,\n" +
  "Biology,,,\n";

const statusCfg = {
  MAP: { label: "Map", color: "#2e7d32", bg: "#e8f5e9" },
  EXISTS: { label: "Skip", color: "#555", bg: "#f0f0f0" },
  ERROR: { label: "Error", color: "#c62828", bg: "#ffebee" },
};

const downloadTemplate = () => {
  const url = URL.createObjectURL(new Blob([TEMPLATE_CSV], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "hierarchy-import-template.csv";
  a.click();
  URL.revokeObjectURL(url);
};

const totalCreated = (created) =>
  created.subjects + created.chapters + created.topics + created.subTopics;

function CountsLine({ created, skipped }) {
  return (
    <Typography sx={{ fontSize: "0.85rem" }}>
      {created.subjects} subject(s), {created.chapters} chapter(s), {created.topics} topic(s),{" "}
      {created.subTopics} sub-topic(s) · {skipped} row(s) skipped (already exist)
    </Typography>
  );
}

// Upload a .xlsx/.csv, preview what will happen (dry run), then confirm. `onImported` fires on
// close only if an import actually happened, so the host screen can reload its list.
export default function BulkImportDialog({ open, onClose, onImported }) {
  const [courses, setCourses] = useState([]);
  const [courseIds, setCourseIds] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState(null);
  const requestId = useRef(0);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setPreview(null);
    setDone(null);
    setPreviewing(false);
    setCourseIds([]);
    listCourses({ includeInactive: false })
      .then(setCourses)
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load exams")));
  }, [open]);

  // Re-run the dry run whenever the file or the chosen courses change; ignore stale responses.
  useEffect(() => {
    if (!file) return undefined;
    const id = ++requestId.current;
    setPreviewing(true);
    bulkImportHierarchy(file, courseIds, true)
      .then((data) => {
        if (id === requestId.current) setPreview(data);
      })
      .catch((err) => {
        if (id === requestId.current) {
          setPreview(null);
          toast.error(apiErrorMessage(err, "Failed to validate the file"));
        }
      })
      .finally(() => {
        if (id === requestId.current) setPreviewing(false);
      });
    return () => {
      requestId.current += 1;
    };
  }, [file, courseIds]);

  const canImport =
    Boolean(preview) && preview.errors.length === 0 && totalCreated(preview.mapped) > 0 && !previewing;

  const handleImport = async () => {
    setImporting(true);
    try {
      const data = await bulkImportHierarchy(file, courseIds, false);
      if (data.errors.length > 0) setPreview(data);
      else setDone(data);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Import failed"));
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    // Don't allow closing mid-import: it would commit without the host screen ever reloading.
    if (importing) return;
    if (done) onImported?.();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <Box sx={{ p: 2.5, background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`, color: "#fff" }}>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ position: "absolute", top: 10, right: 10, color: "rgba(255,255,255,0.85)" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>Bulk Import</Typography>
        <Typography sx={{ fontSize: "0.78rem", opacity: 0.85 }}>
          Add subjects, chapters, topics and sub-topics from one .xlsx or .csv file
        </Typography>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        {done ? (
          <Stack spacing={1.5}>
            <Alert severity="success">Import complete</Alert>
            <CountsLine created={done.mapped} skipped={done.skippedExisting} />
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Typography sx={{ fontSize: "0.85rem" }}>
              Each row defines a path. Leave later columns blank to stop at a higher level. Repeat parent names in
              every row.{" "}
              <Link component="button" type="button" onClick={downloadTemplate}>
                Download template
              </Link>
            </Typography>

            <FormControl fullWidth size="small">
              <InputLabel>Exams for Mapping</InputLabel>
              <Select
                multiple
                label="Exams for Mapping"
                value={courseIds}
                onChange={(e) => setCourseIds(e.target.value)}
                renderValue={(selected) =>
                  courses.filter((c) => selected.includes(c.id)).map((c) => c.name).join(", ")
                }
              >
                {courses.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              component="label"
              variant="contained"
              color="primary"
              startIcon={<UploadFileIcon />}
              sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 600, borderRadius: 2, boxShadow: 2 }}
            >
              {file ? file.name : "Choose File to Upload"}
              <input
                hidden
                type="file"
                data-testid="bulk-import-file"
                accept=".xlsx,.csv"
                onChange={(e) => {
                  setPreview(null);
                  setFile(e.target.files?.[0] || null);
                  e.target.value = "";
                }}
              />
            </Button>

            {previewing && <Typography sx={{ fontSize: "0.85rem" }}>Checking file…</Typography>}

            {preview && (
              <>
                {preview.errors.length > 0 ? (
                  <Alert severity="error">
                    Nothing will be imported until these are fixed:
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {preview.errors.map((e, i) => (
                        <li key={i}>
                          {e.row > 0 ? `Row ${e.row}: ` : ""}
                          {e.errorMessage}
                        </li>
                      ))}
                    </Box>
                  </Alert>
                ) : (
                  <Alert severity="info">
                    <CountsLine created={preview.mapped} skipped={preview.skippedExisting} />
                  </Alert>
                )}

                <Box sx={{ maxHeight: 280, overflow: "auto" }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 70 }}>Row</TableCell>
                        <TableCell sx={{ width: 110 }}>Action</TableCell>
                        <TableCell>Note</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.rows.map((r) => {
                        const cfg = statusCfg[r.status] ?? statusCfg.EXISTS;
                        return (
                          <TableRow key={r.row}>
                            <TableCell>{r.row}</TableCell>
                            <TableCell>
                              <Chip
                                label={cfg.label}
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: 600, fontSize: "0.7rem", color: cfg.color, borderColor: cfg.color, backgroundColor: cfg.bg }}
                              />
                            </TableCell>
                            <TableCell>{r.message}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {done ? (
          <Button variant="contained" onClick={handleClose}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <LoadingButton variant="contained" disabled={!canImport} loading={importing} onClick={handleImport}>
              Import
            </LoadingButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
